import { useEffect } from 'react';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ClaudeIcon } from '@atlassian/logo-third-party/claude';
import { CursorIcon } from '@atlassian/logo-third-party/cursor';
import { OpenAIIcon } from '@atlassian/logo-third-party/openai';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { type NextBestAction } from '@post-office/confluence-next-best-action';
import { FormattedMessage } from 'react-intl';

import type { JSX } from 'react/jsx-runtime';

import { registeredContent } from './content';

const styles = cssMap({
	root: {
		position: 'relative',
		width: '100%',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
	},
	content: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	closeButton: {
		position: 'absolute',
		top: token('space.100'),
		right: token('space.100'),
	},
	callToAction: {
		cursor: 'pointer',
		padding: '0',
		backgroundColor: 'transparent',
		'&:hover': {
			textDecoration: 'underline',
			textDecorationColor: token('color.link'),
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	logos: {
		width: 'fit-content',
	},
	logo: {
		display: 'flex',
		position: 'relative',
		marginRight: token('space.negative.075'),
	},
	logoLast: {
		marginRight: token('space.0'),
	},
	logoTiltLeft: {
		transform: 'rotate(-6deg)',
	},
	logoTiltRight: {
		transform: 'rotate(6deg)',
	},
});

export type ConfluenceAgentsMcpNudgeBannerProps = {
	onDismiss?: () => void;
	onSetup: (action: NextBestAction) => void;
};

type ConfluenceAgentsMcpNudgeBannerContentProps = {
	onDismiss: () => void;
	onSetupClick: () => void;
};

export const ConfluenceAgentsMcpNudgeBannerContent = ({
	onDismiss,
	onSetupClick,
}: ConfluenceAgentsMcpNudgeBannerContentProps): JSX.Element => (
	<Box xcss={styles.root} testId="confluence-agents-mcp-nudge-banner">
		<Stack xcss={styles.content} space="space.100">
			<Box xcss={styles.logos}>
				<Inline alignBlock="center" space="space.0" aria-hidden>
					<Box xcss={cx(styles.logo, styles.logoTiltLeft)}>
						<ClaudeIcon size="xsmall" label="" />
					</Box>
					<Box xcss={cx(styles.logo, styles.logoTiltRight)}>
						<OpenAIIcon size="xsmall" label="" />
					</Box>
					<Box xcss={cx(styles.logo, styles.logoLast, styles.logoTiltLeft)}>
						<CursorIcon size="xsmall" label="" />
					</Box>
				</Inline>
			</Box>
			<Heading size="small">
				<FormattedMessage {...registeredContent.default.heading} />
			</Heading>
			<Text size="small" color="color.text.subtle">
				<FormattedMessage {...registeredContent.default.body} />
			</Text>
			<Box>
				<Pressable xcss={styles.callToAction} aria-haspopup="dialog" onClick={onSetupClick}>
					<Text color="color.link" size="medium">
						<FormattedMessage {...registeredContent.default.callToAction} />
					</Text>
				</Pressable>
			</Box>
		</Stack>
		<Box xcss={styles.closeButton}>
			<IconButton
				appearance="subtle"
				spacing="compact"
				icon={(props) => <CrossIcon {...props} size="small" />}
				label={<FormattedMessage {...registeredContent.default.dismiss} />}
				isTooltipDisabled={false}
				onClick={onDismiss}
			/>
		</Box>
	</Box>
);

export const ConfluenceAgentsMcpNudgeBanner = ({
	onDismiss,
	onSetup,
}: ConfluenceAgentsMcpNudgeBannerProps): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const { onCtaActioned, onClosed } = usePostOfficeUserInteractions();
	const { messageInstanceViewed } = useMessageInstanceEvent();
	const { messageDismissed } = useMessageEvent();

	useEffect(() => {
		if (messageInstanceViewed) {
			void messageInstanceViewed();
		}
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'ConfluenceAgentsMcpNudgeBanner',
		});
		// The viewed event is intentionally emitted once for each fresh message mount.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleDismiss = () => {
		onClosed({ interactionIdentifier: 'dismiss', additionalMetadata: {} });
		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'confluenceAgentsMcpNudgeBannerDismiss',
		});
		void messageDismissed();
		onDismiss?.();
	};

	const handleSetupClick = () => {
		void onCtaActioned({ interactionIdentifier: 'setUpMcp' });

		fireAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'confluenceAgentsMcpNudgeBannerSetUpMcp',
		});
		void messageDismissed();
		onSetup('MCP_SETUP_NUDGE');
	};

	return (
		<AnalyticsContext data={{ source: 'confluence-agents-mcp-nudge-banner' }}>
			<ConfluenceAgentsMcpNudgeBannerContent
				onDismiss={handleDismiss}
				onSetupClick={handleSetupClick}
			/>
		</AnalyticsContext>
	);
};
